import { ChartDataPoint } from 'features/systemLens'
import {
  TimeSeriesDataPoint,
  TimeSeriesResponse,
} from 'features/timeSeries/timeSeriesService'

const pad2 = (value: number): string => (value < 10 ? `0${value}` : `${value}`)

/**
 * Local-time `YYYY-MM-DD HH:mm:ss`, equivalent to
 * `dayjs(iso).utc().local().format('YYYY-MM-DD HH:mm:ss')` but without
 * constructing a dayjs object per point.
 *
 * The `.utc().local()` round-trip in the original was a no-op: only the `utc`
 * plugin is registered (no `timezone`/`setDefault`), so `.local()` returns to
 * system-local mode and `format` renders local either way.
 */
export const formatLocalChartTime = (epochMs: number): string => {
  const date = new Date(epochMs)
  return (
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` +
    ` ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
  )
}

/**
 * Collapses the per-sensor response into chart rows keyed by timestamp.
 *
 * Hot path: this runs over every point of every pen on every fetch. At 25 pens /
 * 7 days that is ~180k points, so per-point allocation is what makes the page
 * freeze rather than merely feel slow.
 */
export const mergeTimeSeriesResponse = (
  response: TimeSeriesResponse
): Record<string, ChartDataPoint> => {
  const allDataPoints: Record<string, ChartDataPoint> = {}

  for (const sensorId of Object.keys(response)) {
    const dataPoints = response[sensorId] as TimeSeriesDataPoint[]
    if (!dataPoints) continue

    for (let index = 0; index < dataPoints.length; index += 1) {
      const point = dataPoints[index]
      const timestamp = point.timestamp
      let row = allDataPoints[timestamp]

      // Parsing and formatting are per-row, not per-point: every pen after the
      // first reuses the row a previous pen created.
      if (row === undefined) {
        const epochMs = Date.parse(timestamp)
        row = {
          timestamp: epochMs,
          time: formatLocalChartTime(epochMs),
        }
        allDataPoints[timestamp] = row
      }

      row[sensorId] = point.value
    }
  }

  return allDataPoints
}

/**
 * Collapse merged rows into the ascending-x array uPlot requires.
 *
 * `mergeTimeSeriesResponse` keys rows by timestamp *string*, so `Object.values`
 * yields insertion order: the order the first pen introduced each bucket. That
 * is ascending only because the server sorts globally and `gap_fill` gives every
 * pen an identical bucket set. Both are remote invariants we do not control, and
 * a single pen with a differing bucket set would append its extra timestamps at
 * the end -- uPlot does not validate x, it just renders a truncated mess, which
 * is indistinguishable to a user from the real "chopped chart" bug.
 *
 * So verify locally. The check is a single O(n) scan on the already-hot merge
 * path and sorts only on the (expected-impossible) violation, making the
 * ordering a local guarantee rather than an assumption about the backend.
 */
export const toAscendingChartRows = (
  rows: Record<string, ChartDataPoint>
): ChartDataPoint[] => {
  const values = Object.values(rows)

  for (let index = 1; index < values.length; index += 1) {
    if (values[index].timestamp < values[index - 1].timestamp) {
      return values.sort((left, right) => left.timestamp - right.timestamp)
    }
  }

  return values
}
